import React, { useState } from 'react';
import { Property } from '../../../types';
import { TrashIcon, PlusIcon } from '../../../components/Icons';
import { PropertyTagsEditor } from '../../../components/admin/property_edit/PropertyTagsEditor';
import { PropertyImageGalleryEditor } from './PropertyImageGalleryEditor';

interface Props {
  formData: Partial<Property>;
  onChange: (field: keyof Property, value: any) => void;
}

export const PropertyMediaFeaturesFields: React.FC<Props> = ({ formData, onChange }) => {
  const [newFeature, setNewFeature] = useState('');

  const features = Array.isArray(formData.features) ? formData.features : [];
  const rawImages = (Array.isArray(formData.images) && formData.images.length > 0)
    ? formData.images
    : (Array.isArray((formData as any).imagenes) ? (formData as any).imagenes : []);
  const imageUrls = rawImages.map((img: any) => (typeof img === 'string' ? img : img?.url || '')).filter(Boolean);

  const addFeature = () => {
    if (!newFeature.trim()) return;
    onChange('features', [...features, newFeature.trim()]);
    setNewFeature('');
  };

  const removeFeature = (idx: number) => {
    onChange('features', features.filter((_, i) => i !== idx));
  };

  const handleImagesChange = (next: string[]) => {
    onChange('images', next);
    onChange('imagenes' as any, next);
  };

  const handleTagsChange = (nextTags: string[]) => {
    onChange('tag', nextTags);
    onChange('tags', nextTags);
  };

  return (
    <div className="space-y-4 text-xs">
      <div>
        <label className="font-bold text-gray-700 block mb-1">Descripción Completa</label>
        <textarea
          rows={3}
          value={formData.descripcion || ''}
          onChange={(e) => onChange('descripcion', e.target.value)}
          className="w-full p-2 border rounded-lg focus:ring-sky-500"
          placeholder="Descripción detallada del inmueble..."
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="font-bold text-gray-700 block mb-1">Nombre Contacto</label>
          <input
            type="text"
            value={formData.nombre_cliente || (formData as any).contacto_nombre || ''}
            onChange={(e) => {
              onChange('nombre_cliente', e.target.value);
              onChange('contacto_nombre' as any, e.target.value);
            }}
            className="w-full p-2 border rounded-lg"
            placeholder="Ej: Juan Pérez"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Teléfono Contacto</label>
          <input
            type="text"
            value={formData.telefono_cliente || (formData as any).contacto_telefono || ''}
            onChange={(e) => {
              onChange('telefono_cliente', e.target.value);
              onChange('contacto_telefono' as any, e.target.value);
            }}
            className="w-full p-2 border rounded-lg"
            placeholder="Ej: 3001234567"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Correo Contacto</label>
          <input
            type="email"
            value={formData.correo_1 || (formData as any).correo_contacto || ''}
            onChange={(e) => {
              onChange('correo_1', e.target.value);
              onChange('correo_contacto' as any, e.target.value);
            }}
            className="w-full p-2 border rounded-lg"
            placeholder="Ej: contacto@ejemplo.com"
          />
        </div>
        <div>
          <label className="font-bold text-gray-700 block mb-1">Recorrido Virtual / YouTube</label>
          <input
            type="url"
            value={formData.video_youtube_url || (formData as any).link_recorrido_virtual || ''}
            onChange={(e) => {
              onChange('video_youtube_url', e.target.value);
              onChange('link_recorrido_virtual' as any, e.target.value);
            }}
            className="w-full p-2 border rounded-lg"
            placeholder="https://youtube.com/..."
          />
        </div>
      </div>

      {/* Editor modular de Tags del Inmueble */}
      <PropertyTagsEditor
        tags={formData.tag || formData.tags || []}
        onChange={handleTagsChange}
        idPrefix="admin-edit-property-tags"
      />

      {/* Características / Amenities */}
      <div>
        <label className="font-bold text-gray-700 block mb-1">Características / Amenities</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
            placeholder="Ej: Piscina, Gimnasio, Balcón"
            className="flex-grow p-2 border rounded-lg"
          />
          <button type="button" onClick={addFeature} className="px-3 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-bold">
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-100">
          {features.map((tag, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-[11px] font-medium text-gray-700">
              {tag}
              <button type="button" onClick={() => removeFeature(idx)} className="text-red-500 hover:text-red-700"><TrashIcon className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>

      {/* Galería de imágenes modular */}
      <PropertyImageGalleryEditor
        images={imageUrls}
        onChange={handleImagesChange}
      />
    </div>
  );
};
